import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListChecks, CheckCircle, Circle, Warning, Fire, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * MyTasksBanner
 * -------------
 * Compact "// YOUR TASKS" card that highlights pending tasks assigned to
 * the currently-logged-in user. Designed to sit at the top of Home /
 * Operator / Fabrication / Delivery panels so staff never miss a task the
 * owner has allotted to them.
 *
 * - Fetches `/tasks?scope=mine&status=pending`
 * - Renders top 5 pending items with priority-coloured left border
 * - Tap the circle to mark done in-place
 * - `onOpenAll` (optional) lets the parent link to the full Tasks page
 */
export default function MyTasksBanner({ onOpenAll, includeUnassigned = false, className = "" }) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("scope", "mine");
      params.set("status", "pending");
      const { data } = await api.get(`/tasks?${params.toString()}`);
      let list = data || [];
      if (includeUnassigned) {
        try {
          const { data: un } = await api.get("/tasks?scope=unassigned&status=pending");
          const seen = new Set(list.map((t) => t.id));
          list = [...list, ...(un || []).filter((t) => !seen.has(t.id))];
        } catch (_e) { /* ignore */ }
      }
      setTasks(list.slice(0, 5));
    } catch (_e) { /* silent */ } finally { setLoading(false); }
  }, [user, includeUnassigned]);

  useEffect(() => { load(); }, [load]);

  const markDone = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: "done" });
      toast.success("Task done 🎯");
      load();
    } catch (_e) {
      toast.error("Task update failed");
    }
  };

  if (loading || tasks.length === 0) return null;

  return (
    <div
      className={
        "surface rounded-sm p-4 border-l-4 border-l-orange-500 " +
        "shadow-[0_0_25px_rgba(255,87,34,0.10)] " + className
      }
      data-testid="my-tasks-banner"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm bg-orange-500/15 border border-orange-500/40 flex items-center justify-center">
            <ListChecks size={16} className="text-orange-400" weight="fill" />
          </div>
          <div>
            <div className="text-[10px] font-mono-num tracking-widest text-orange-400">// YOUR TASKS</div>
            <div className="text-white font-heading text-sm">
              {tasks.length} pending task{tasks.length > 1 ? "s" : ""}
            </div>
          </div>
        </div>
        {onOpenAll && (
          <button
            onClick={onOpenAll}
            data-testid="my-tasks-open-all"
            className="text-[11px] font-mono-num tracking-widest text-orange-300 hover:text-orange-200 border border-orange-500/40 rounded-sm px-2 py-1"
          >
            OPEN ALL →
          </button>
        )}
      </div>
      <div className="space-y-2">
        {tasks.map((t) => {
          const prioClr =
            t.priority === "high"
              ? "border-l-red-500 bg-red-500/5"
              : t.priority === "low"
              ? "border-l-slate-600 bg-slate-800/40"
              : "border-l-amber-500 bg-amber-500/5";
          const overdue = t.due_date && new Date(t.due_date + "T23:59:59") < new Date();
          return (
            <div
              key={t.id}
              className={"flex items-start gap-2 p-2 rounded-sm border-l-4 " + prioClr}
              data-testid={`my-tasks-row-${t.id}`}
            >
              <button
                onClick={() => markDone(t)}
                className="mt-0.5 shrink-0 text-slate-400 hover:text-emerald-400 transition"
                data-testid={`my-tasks-done-${t.id}`}
                title="Mark done"
              >
                <Circle size={18} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{t.title}</div>
                <div className="text-[10px] font-mono-num text-slate-500 flex items-center gap-2 mt-0.5">
                  {t.priority === "high" && (
                    <span className="text-red-300 flex items-center gap-0.5">
                      <Fire size={10} weight="fill" /> HIGH
                    </span>
                  )}
                  {t.due_date && (
                    <span className={"flex items-center gap-0.5 " + (overdue ? "text-red-300" : "")}>
                      <Calendar size={10} /> {new Date(t.due_date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                      {overdue && <Warning size={10} weight="fill" className="ml-0.5" />}
                    </span>
                  )}
                  {!t.assigned_to_user_id && <span className="text-cyan-300">UNASSIGNED</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
